import { useState, useEffect } from 'react';
import { useAuth, API } from '../../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, User, Mail, Phone, Shield, Search, MapPin, ExternalLink, History, ArrowRight, Clock, MapPinOff, Star, TrendingUp, ChevronDown, ChevronUp, Target, Sparkles, Loader2, Save, RefreshCw, Eye, Settings, Lock, Smartphone, Download, UserX, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import PermissionsModal from './PermissionsModal';
import UserDevicesDialog from './UserDevicesDialog';
import DriverRidesExportDialog from './DriverRidesExportDialog';


export default function UserManagement() {
  const { token, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [filterRole, setFilterRole] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [driverRatings, setDriverRatings] = useState(null);
  const [ratingsError, setRatingsError] = useState(null);
  const [ratingsMonth, setRatingsMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });
  const [selectedDriverHistory, setSelectedDriverHistory] = useState(null);
  const [showRatingHistory, setShowRatingHistory] = useState(false);
  const [expandedRatings, setExpandedRatings] = useState({});
  const [expandedMonths, setExpandedMonths] = useState({});
  const [monthRides, setMonthRides] = useState({});
  const [loadingMonthRides, setLoadingMonthRides] = useState({});
  const [permissionsUser, setPermissionsUser] = useState(null);
  const [devicesUser, setDevicesUser] = useState(null);
  const [exportDriver, setExportDriver] = useState(null); // { id, name, year, month } | null

  // Inline rating edit state
  const [expandedRideRating, setExpandedRideRating] = useState(null); // ride id being edited
  const [rideRatingAdjustments, setRideRatingAdjustments] = useState({});
  const [savingRideRating, setSavingRideRating] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'driver',
    phone: '',
    gps_device_id: '',
    location_restricted: true
  });

  useEffect(() => {
    fetchUsers();
    fetchAuditLogs();
  }, []);

  // Refetch ratings whenever the selected month changes
  useEffect(() => {
    fetchDriverRatings();
  }, [ratingsMonth]);

  const fetchAuditLogs = async () => {
    try {
      const response = await axios.get(`${API}/audit-logs?action=role_change&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  };

  const fetchDriverRatings = async () => {
    try {
      setRatingsError(null);
      const { year, month } = ratingsMonth;
      const response = await axios.get(
        `${API}/drivers/ratings/current-month?year=${year}&month=${month}`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 90000 }
      );
      setDriverRatings(response.data);
    } catch (error) {
      console.error('Error fetching driver ratings:', error);
      setRatingsError(error.response?.data?.detail || 'Failed to load driver performance - the server took too long or returned an error.');
    }
  };

  const fetchDriverHistory = async (driverId) => {
    try {
      const response = await axios.get(`${API}/drivers/${driverId}/rating/history?months=12`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedDriverHistory(response.data);
      setShowRatingHistory(true);
      // Reset expanded months when opening new history
      setExpandedMonths({});
      setMonthRides({});
    } catch (error) {
      console.error('Error fetching driver history:', error);
      toast.error('Failed to load rating history');
    }
  };

  const fetchMonthRides = async (driverId, year, month) => {
    const monthKey = `${year}-${month}`;
    setLoadingMonthRides(prev => ({ ...prev, [monthKey]: true }));
    try {
      const response = await axios.get(`${API}/drivers/${driverId}/rating/rides?year=${year}&month=${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonthRides(prev => ({ ...prev, [monthKey]: response.data.rides }));
    } catch (error) {
      console.error('Error fetching month rides:', error);
      toast.error('Failed to load rides for this month');
    } finally {
      setLoadingMonthRides(prev => ({ ...prev, [monthKey]: false }));
    }
  };

  const toggleMonthExpanded = (driverId, year, month) => {
    const monthKey = `${year}-${month}`;
    const isCurrentlyExpanded = expandedMonths[monthKey];
    
    if (!isCurrentlyExpanded && !monthRides[monthKey]) {
      // Load rides when expanding for the first time
      fetchMonthRides(driverId, year, month);
    }
    
    setExpandedMonths(prev => ({ ...prev, [monthKey]: !isCurrentlyExpanded }));
  };

  // Helper to get score from rating (handles both formats)
  const getRatingScore = (rating, field) => {
    if (!rating || !rating[field]) return null;
    const val = rating[field];
    if (typeof val === 'object') return val.score;
    return val;
  };

  const getScoreColor = (score) => {
    if (score === null || score === undefined) return 'text-gray-400';
    if (score >= 90) return 'text-green-600';
    if (score >= 75) return 'text-yellow-600';
    if (score >= 50) return 'text-orange-500';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score === null || score === undefined) return 'bg-gray-100';
    if (score >= 90) return 'bg-green-100';
    if (score >= 75) return 'bg-yellow-100';
    if (score >= 50) return 'bg-orange-100';
    return 'bg-red-100';
  };

  // Toggle ride rating edit panel
  const toggleRideRatingEdit = (ride) => {
    if (expandedRideRating === ride.id) {
      setExpandedRideRating(null);
      return;
    }
    setExpandedRideRating(ride.id);
    setRideRatingAdjustments({
      punctuality_cause: ride.punctuality_cause || 'driver_fault',
      reliability_cause: ride.reliability_cause || 'driver_fault',
      has_service_complaint: ride.has_service_complaint || false,
      no_pickup_by_driver: ride.no_pickup_by_driver || false
    });
  };

  // Save ride rating adjustments
  const saveRideRating = async (rideId, monthKey) => {
    setSavingRideRating(rideId);
    try {
      const response = await axios.put(`${API}/rides/${rideId}/rating`, rideRatingAdjustments, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rating updated');
      // Refresh rides for this month
      const [year, month] = monthKey.split('-');
      if (selectedDriverHistory?.driver_id) {
        await fetchMonthRides(selectedDriverHistory.driver_id, parseInt(year), parseInt(month));
      }
      setExpandedRideRating(null);
      // Refresh history and ratings
      if (selectedDriverHistory?.driver_id) {
        fetchDriverHistory(selectedDriverHistory.driver_id);
      }
      fetchDriverRatings();
    } catch (error) {
      console.error('Error saving rating:', error);
      toast.error('Error saving');
    } finally {
      setSavingRideRating(null);
    }
  };

  // Recalculate ride rating
  const recalculateRideRating = async (rideId, monthKey) => {
    setSavingRideRating(rideId);
    try {
      await axios.post(`${API}/rides/${rideId}/recalculate-rating`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rating recalculated');
      const [year, month] = monthKey.split('-');
      if (selectedDriverHistory?.driver_id) {
        await fetchMonthRides(selectedDriverHistory.driver_id, parseInt(year), parseInt(month));
      }
      setExpandedRideRating(null);
      if (selectedDriverHistory?.driver_id) {
        fetchDriverHistory(selectedDriverHistory.driver_id);
      }
      fetchDriverRatings();
    } catch (error) {
      console.error('Error recalculating:', error);
      toast.error('Error recalculating');
    } finally {
      setSavingRideRating(null);
    }
  };

  // Navigate to ride in Past Rides
  const goToRideDetails = (ride) => {
    if (!ride.date_time) return;
    const d = new Date(ride.date_time);
    const month = d.getMonth() + 1;
    const year = d.getFullYear();
    navigate(`/admin/past-rides?month=${month}&year=${year}&searchTerm=${encodeURIComponent(ride.customer_name || '')}`);
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.name || !formData.role) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error('Password is required for new users');
      return;
    }

    const isDriverRole = formData.role === 'driver' || formData.role === 'admin-driver';
    const normalizedPhone = (formData.phone || '').replace(/\s+/g, '').trim();
    if (isDriverRole) {
      if (!normalizedPhone) {
        toast.error('Il numero di telefono è obbligatorio per i driver');
        return;
      }
      if (!/^\+[1-9]\d{6,14}$/.test(normalizedPhone)) {
        toast.error('Formato telefono non valido: usa il formato internazionale con prefisso, es. +301234567890');
        return;
      }
    }

    try {
      const payload = {
        email: formData.email,
        name: formData.name,
        role: formData.role,
        phone: normalizedPhone || null,
        gps_device_id: formData.gps_device_id || null,
        location_restricted: formData.location_restricted
      };

      if (editingUser) {
        // Update user
        if (formData.password) {
          payload.password = formData.password;
        }
        const roleChanged = editingUser.role !== formData.role;
        await axios.put(`${API}/users/${editingUser.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('User updated successfully');
        if (roleChanged) {
          fetchAuditLogs(); // Refresh audit logs if role changed
        }
      } else {
        // Create new user
        payload.password = formData.password;
        await axios.post(`${API}/auth/register`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('User created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleToggleActive = async (user) => {
    const activating = user.is_active === false;
    if (!activating && !window.confirm(`Deactivate ${user.name}? They will disappear from driver lists and won't be able to log in. The profile is NOT deleted.`)) return;
    try {
      await axios.put(`${API}/users/${user.id}`, { is_active: activating }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(activating ? `${user.name} reactivated` : `${user.name} deactivated`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update account status');
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"?`)) return;

    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name,
      role: user.role,
      phone: user.phone || '',
      gps_device_id: user.gps_device_id || '',
      location_restricted: user.location_restricted !== false // Default to true if undefined
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'driver',
      phone: '',
      gps_device_id: '',
      location_restricted: true
    });
    setEditingUser(null);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      case 'admin-driver':
        return 'bg-indigo-100 text-indigo-800';
      case 'driver':
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesSearch = !searchTerm || 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesRole && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <>
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" data-testid="users-management-title">User Management</h1>
          <p className="text-slate-600 mt-2">Manage admin users and drivers</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm shadow-sm"
              data-testid="create-user-button"
            >
              <Plus className="mr-2 h-5 w-5" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
              <DialogDescription>
                {editingUser ? 'Update user information' : 'Add a new admin or driver to the system'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5 mt-4" data-testid="user-form">
              <div>
                <Label htmlFor="name" className="text-slate-700 font-medium mb-2 block">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                  className="h-11 rounded-sm"
                  data-testid="name-input"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-slate-700 font-medium mb-2 block">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@outsiders.com"
                  required
                  disabled={editingUser}
                  className="h-11 rounded-sm"
                  data-testid="email-input"
                />
                {editingUser && (
                  <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
                )}
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-700 font-medium mb-2 block">
                  Password {editingUser ? '(leave blank to keep current)' : '*'}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={editingUser ? 'Enter new password to change' : 'Enter password'}
                  required={!editingUser}
                  className="h-11 rounded-sm"
                  data-testid="password-input"
                />
              </div>

              <div>
                <Label htmlFor="role" className="text-slate-700 font-medium mb-2 block">
                  Role *
                </Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger className="h-11 rounded-sm" data-testid="role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="driver">Driver</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="admin-driver">Admin-Driver</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="phone" className="text-slate-700 font-medium mb-2 block">
                  Phone Number {(formData.role === 'driver' || formData.role === 'admin-driver') && <span className="text-red-500">* obbligatorio (formato +30...)</span>}
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+301234567890"
                  className="h-11 rounded-sm"
                  data-testid="phone-input"
                />
              </div>

              <div>
                <Label htmlFor="gps_device_id" className="text-slate-700 font-medium mb-2 block">
                  GPS Device ID (Salind Tracker)
                </Label>
                <Input
                  id="gps_device_id"
                  type="text"
                  value={formData.gps_device_id}
                  onChange={(e) => setFormData({ ...formData, gps_device_id: e.target.value })}
                  placeholder="e.g. SALIND12345"
                  className="h-11 rounded-sm"
                  data-testid="gps-device-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter the Salind GPS tracker device ID for this driver
                </p>
              </div>

              {/* Location Restriction Toggle - Only show for driver roles */}
              {(formData.role === 'driver' || formData.role === 'admin-driver') && (
                <div className="border border-slate-200 rounded-sm p-4 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {formData.location_restricted ? (
                        <MapPin className="h-5 w-5 text-blue-600" />
                      ) : (
                        <MapPinOff className="h-5 w-5 text-amber-600" />
                      )}
                      <div>
                        <Label htmlFor="location_restricted" className="text-slate-700 font-medium cursor-pointer">
                          GPS Location Restrictions
                        </Label>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {formData.location_restricted 
                            ? "Driver must be near pickup/dropoff to update ride status" 
                            : "Driver can update ride status from any location"}
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="location_restricted"
                      checked={formData.location_restricted}
                      onCheckedChange={(checked) => setFormData({ ...formData, location_restricted: checked })}
                      data-testid="location-restricted-toggle"
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-11"
                  data-testid="submit-user-button"
                >
                  {editingUser ? 'Update User' : 'Create User'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                  className="flex-1 rounded-sm h-11"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm mb-6 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Search className="h-5 w-5 text-slate-500" />
          <h3 className="font-semibold text-slate-900">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-700 text-sm mb-2 block">Search</Label>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
              className="h-10 rounded-sm"
              data-testid="search-input"
            />
          </div>
          <div>
            <Label className="text-slate-700 text-sm mb-2 block">Role</Label>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="h-10 rounded-sm" data-testid="filter-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="driver">Driver</SelectItem>
                <SelectItem value="admin-driver">Admin-Driver</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Driver Ratings Section */}
      {ratingsError && !driverRatings && (
        <div className="bg-white border border-red-200 rounded-sm shadow-sm mb-6 p-5" data-testid="ratings-error-panel">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="h-10 w-10 bg-red-50 rounded-full flex items-center justify-center">
              <Star className="h-5 w-5 text-red-500" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <h3 className="font-semibold text-slate-900">Driver Performance Ratings</h3>
              <p className="text-sm text-red-600">{ratingsError}</p>
            </div>
            <Button onClick={fetchDriverRatings} variant="outline" data-testid="ratings-retry-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Retry
            </Button>
          </div>
        </div>
      )}
      {driverRatings && driverRatings.drivers && (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden mb-6">
          <div className="px-6 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <Star className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Driver Performance Ratings</h3>
                  <p className="text-sm text-slate-600">
                    {new Date(driverRatings.year, driverRatings.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </p>
                </div>
              </div>
              {/* Month/year picker */}
              <div className="flex items-center gap-2" data-testid="ratings-month-picker">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRatingsMonth((s) => {
                    const m = s.month === 1 ? 12 : s.month - 1;
                    const y = s.month === 1 ? s.year - 1 : s.year;
                    return { year: y, month: m };
                  })}
                  className="h-8 px-2"
                  data-testid="ratings-prev-month"
                >
                  <ChevronDown className="h-4 w-4 -rotate-90" />
                </Button>
                <select
                  value={ratingsMonth.month}
                  onChange={(e) => setRatingsMonth((s) => ({ ...s, month: parseInt(e.target.value, 10) }))}
                  className="border border-slate-300 rounded h-8 text-sm px-2 bg-white"
                  data-testid="ratings-month-select"
                >
                  {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, idx) => (
                    <option key={m} value={idx + 1}>{m}</option>
                  ))}
                </select>
                <select
                  value={ratingsMonth.year}
                  onChange={(e) => setRatingsMonth((s) => ({ ...s, year: parseInt(e.target.value, 10) }))}
                  className="border border-slate-300 rounded h-8 text-sm px-2 bg-white"
                  data-testid="ratings-year-select"
                >
                  {(() => {
                    const currY = new Date().getFullYear();
                    const years = [];
                    for (let y = currY - 3; y <= currY + 1; y++) years.push(y);
                    return years.map((y) => <option key={y} value={y}>{y}</option>);
                  })()}
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRatingsMonth((s) => {
                    const m = s.month === 12 ? 1 : s.month + 1;
                    const y = s.month === 12 ? s.year + 1 : s.year;
                    return { year: y, month: m };
                  })}
                  className="h-8 px-2"
                  data-testid="ratings-next-month"
                >
                  <ChevronDown className="h-4 w-4 rotate-90" />
                </Button>
              </div>
            </div>
          </div>
          <div className="p-4">
            <div className="grid gap-4">
              {(() => {
                // Show ALL drivers, so History/Export stay accessible even in months with no rides
                const sorted = [...driverRatings.drivers].sort((a, b) => (b.total_rides || 0) - (a.total_rides || 0));
                if (sorted.length === 0) {
                  return (
                    <div className="text-sm text-slate-500 text-center py-6">
                      No drivers found.
                    </div>
                  );
                }
                return sorted.map((driver) => (
                <div key={driver.driver_id} className={`border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors ${driver.total_rides === 0 ? 'opacity-70' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
                        <User className="h-6 w-6 text-slate-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">{driver.driver_name}</h4>
                        <p className="text-sm text-slate-500">{driver.total_rides} rides this month</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className={`text-center px-4 py-2 rounded-lg ${getScoreBgColor(driver.average_score)}`}>
                        <div className={`text-2xl font-bold ${getScoreColor(driver.average_score)}`}>
                          {driver.average_score !== null ? driver.average_score.toFixed(1) : 'N/A'}
                        </div>
                        <div className="text-xs text-slate-600">Average</div>
                      </div>
                      {driver.breakdown && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (expandedRatings[driver.driver_id]) {
                            setExpandedRatings({ ...expandedRatings, [driver.driver_id]: false });
                          } else {
                            setExpandedRatings({ ...expandedRatings, [driver.driver_id]: true });
                          }
                        }}
                        className="px-2"
                      >
                        {expandedRatings[driver.driver_id] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchDriverHistory(driver.driver_id)}
                      >
                        <History className="h-4 w-4 mr-1" />
                        History
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setExportDriver({
                          id: driver.driver_id,
                          name: driver.driver_name,
                          year: ratingsMonth.year,
                          month: ratingsMonth.month,
                        })}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid={`ratings-export-${driver.driver_id}`}
                        title="Export monthly rides"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Export
                      </Button>
                    </div>
                  </div>
                  
                  {/* Expanded breakdown */}
                  {expandedRatings[driver.driver_id] && driver.breakdown && (
                    <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className={`text-lg font-semibold ${getScoreColor(driver.breakdown.ride_management)}`}>
                          {driver.breakdown.ride_management?.toFixed(1) || 'N/A'}
                        </div>
                        <div className="text-xs text-slate-500">Management (35%)</div>
                      </div>
                      <div className="text-center">
                        <div className={`text-lg font-semibold ${getScoreColor(driver.breakdown.reliability)}`}>
                          {driver.breakdown.reliability?.toFixed(1) || 'N/A'}
                        </div>
                        <div className="text-xs text-slate-500">Reliability (25%)</div>
                      </div>
                      <div className="text-center">
                        <div className={`text-lg font-semibold ${getScoreColor(driver.breakdown.service_quality)}`}>
                          {driver.breakdown.service_quality?.toFixed(1) || 'N/A'}
                        </div>
                        <div className="text-xs text-slate-500">Quality (5%)</div>
                      </div>
                      <div className="text-center">
                        <div className={`text-lg font-semibold ${getScoreColor(driver.breakdown.punctuality)}`}>
                          {driver.breakdown.punctuality?.toFixed(1) || 'N/A'}
                        </div>
                        <div className="text-xs text-slate-500">Punctuality (35%)</div>
                      </div>
                    </div>
                  )}
                </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Rating History Dialog */}
      <Dialog open={showRatingHistory} onOpenChange={setShowRatingHistory}>
        <DialogContent className="max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Rating History</DialogTitle>
            <DialogDescription>
              Performance scores for the past 12 months - Click on a month to see ride details
            </DialogDescription>
          </DialogHeader>
          {selectedDriverHistory && (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2">
              {selectedDriverHistory.history.map((month, index) => {
                const monthKey = `${month.year}-${month.month}`;
                const isExpanded = expandedMonths[monthKey];
                const rides = monthRides[monthKey] || [];
                const isLoading = loadingMonthRides[monthKey];
                
                return (
                  <div key={monthKey} className="border rounded-lg overflow-hidden">
                    {/* Month Header - Clickable */}
                    <div 
                      className={`flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50 transition-colors ${isExpanded ? 'bg-slate-50 border-b' : ''}`}
                      onClick={() => month.total_rides > 0 && toggleMonthExpanded(selectedDriverHistory.driver_id, month.year, month.month)}
                    >
                      <div className="flex items-center gap-3">
                        {month.total_rides > 0 && (
                          <div className="text-slate-400">
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </div>
                        )}
                        <div>
                          <div className="font-medium">
                            {new Date(month.year, month.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                          </div>
                          <div className="text-sm text-slate-500">{month.total_rides} rides</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {/* Breakdown Summary */}
                        {month.breakdown && month.total_rides > 0 && (
                          <div className="hidden sm:flex items-center gap-2 text-xs">
                            <span className="text-sky-600" title="Punctuality">
                              <Clock className="h-3 w-3 inline mr-0.5" />
                              {month.breakdown.punctuality !== null ? Math.round(month.breakdown.punctuality) : 'N/A'}
                            </span>
                            <span className="text-violet-600" title="Management">
                              <Target className="h-3 w-3 inline mr-0.5" />
                              {Math.round(month.breakdown.ride_management || 0)}
                            </span>
                            <span className="text-emerald-600" title="Reliability">
                              <Shield className="h-3 w-3 inline mr-0.5" />
                              {Math.round(month.breakdown.reliability || 0)}
                            </span>
                            <span className="text-amber-600" title="Quality">
                              <Sparkles className="h-3 w-3 inline mr-0.5" />
                              {Math.round(month.breakdown.service_quality || 0)}
                            </span>
                          </div>
                        )}
                        <div className={`text-xl font-bold ${getScoreColor(month.average_score)}`}>
                          {month.average_score !== null ? month.average_score.toFixed(1) : 'N/A'}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExportDriver({
                              id: selectedDriverHistory.driver_id,
                              name: selectedDriverHistory.driver_name,
                              year: month.year,
                              month: month.month,
                            });
                          }}
                          className="h-8 w-8 hover:bg-amber-50 hover:text-amber-600"
                          data-testid={`history-export-${month.year}-${month.month}`}
                          title="Export this month's rides"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Expanded Rides List */}
                    {isExpanded && (
                      <div className="bg-slate-50 p-3">
                        {isLoading ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                            <span className="ml-2 text-sm text-slate-500">Loading rides...</span>
                          </div>
                        ) : rides.length === 0 ? (
                          <p className="text-sm text-slate-500 text-center py-2">No rides found for this month</p>
                        ) : (
                          <div className="space-y-2">
                            {rides.map((ride, rideIndex) => (
                              <div key={ride.id || rideIndex} className="bg-white rounded-lg p-3 border border-slate-200">
                                <div className="flex justify-between items-start mb-2">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm">{ride.customer_name || 'Unknown Customer'}</p>
                                    <p className="text-xs text-slate-500">
                                      {ride.date_time ? new Date(ride.date_time).toLocaleString('it-IT', {
                                        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                      }) : 'N/A'}
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1 truncate max-w-[200px]">
                                      {ride.pickup_location || 'N/A'} → {ride.dropoff_location || 'N/A'}
                                    </p>
                                  </div>
                                  <div className="text-right flex flex-col items-end gap-1">
                                    <div className={`text-lg font-bold ${getScoreColor(ride.rating_total_score)}`}>
                                      {ride.rating_total_score !== null ? ride.rating_total_score.toFixed(0) : 'N/A'}
                                    </div>
                                    <div className={`text-xs px-2 py-0.5 rounded ${
                                      ride.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                                    }`}>
                                      {ride.status}
                                    </div>
                                  </div>
                                </div>
                                {/* Rating Breakdown for this ride */}
                                {ride.rating && (
                                  <div className="grid grid-cols-4 gap-2 mt-2 pt-2 border-t border-slate-100">
                                    <div className="text-center">
                                      <p className="text-[10px] text-slate-500 uppercase">Punctuality</p>
                                      <p className="text-sm font-semibold text-sky-600">
                                        {getRatingScore(ride.rating, 'punctuality') !== null 
                                          ? Math.round(getRatingScore(ride.rating, 'punctuality')) 
                                          : 'N/A'}
                                      </p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-[10px] text-slate-500 uppercase">Management</p>
                                      <p className="text-sm font-semibold text-violet-600">
                                        {Math.round(getRatingScore(ride.rating, 'ride_management') || 0)}
                                      </p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-[10px] text-slate-500 uppercase">Reliability</p>
                                      <p className="text-sm font-semibold text-emerald-600">
                                        {Math.round(getRatingScore(ride.rating, 'reliability') || 0)}
                                      </p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-[10px] text-slate-500 uppercase">Quality</p>
                                      <p className="text-sm font-semibold text-amber-600">
                                        {Math.round(getRatingScore(ride.rating, 'service_quality') || 0)}
                                      </p>
                                    </div>
                                  </div>
                                )}
                                {/* Action Buttons */}
                                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
                                  <button
                                    onClick={() => goToRideDetails(ride)}
                                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                                    data-testid={`ride-details-link-${rideIndex}`}
                                  >
                                    <Eye className="h-3 w-3" />
                                    Ride Details
                                  </button>
                                  {(ride.status === 'completed' || ride.status === 'no_show') && (
                                    <button
                                      onClick={() => toggleRideRatingEdit(ride)}
                                      className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
                                        expandedRideRating === ride.id 
                                          ? 'text-amber-800 bg-amber-100' 
                                          : 'text-amber-600 hover:text-amber-800 hover:bg-amber-50'
                                      }`}
                                      data-testid={`edit-rating-btn-${rideIndex}`}
                                    >
                                      <Settings className="h-3 w-3" />
                                      Edit Rating
                                    </button>
                                  )}
                                </div>
                                {/* Inline Rating Edit Panel */}
                                {expandedRideRating === ride.id && (
                                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3" data-testid={`rating-edit-panel-${rideIndex}`}>
                                    <p className="text-xs font-semibold text-amber-800 flex items-center gap-1">
                                      <Star className="h-3 w-3" />
                                      Edit Rating
                                    </p>
                                    {/* Punctuality Cause */}
                                    <div className="flex items-center justify-between">
                                      <label className="text-xs text-slate-700">Delay Cause</label>
                                      <select
                                        value={rideRatingAdjustments.punctuality_cause}
                                        onChange={(e) => setRideRatingAdjustments(prev => ({ ...prev, punctuality_cause: e.target.value }))}
                                        className="text-xs border border-slate-300 rounded px-2 py-1 bg-white"
                                        data-testid={`punctuality-cause-select-${rideIndex}`}
                                      >
                                        <option value="driver_fault">Driver Fault</option>
                                        <option value="external_causes">External Causes</option>
                                      </select>
                                    </div>
                                    {/* Reliability Cause */}
                                    <div className="flex items-center justify-between">
                                      <label className="text-xs text-slate-700">Reliability Cause</label>
                                      <select
                                        value={rideRatingAdjustments.reliability_cause}
                                        onChange={(e) => setRideRatingAdjustments(prev => ({ ...prev, reliability_cause: e.target.value }))}
                                        className="text-xs border border-slate-300 rounded px-2 py-1 bg-white"
                                        data-testid={`reliability-cause-select-${rideIndex}`}
                                      >
                                        <option value="driver_fault">Driver Fault</option>
                                        <option value="external_causes">External Causes</option>
                                      </select>
                                    </div>
                                    {/* Service Complaint Toggle */}
                                    <div className="flex items-center justify-between">
                                      <label className="text-xs text-slate-700">Customer Complaint</label>
                                      <button
                                        onClick={() => setRideRatingAdjustments(prev => ({ ...prev, has_service_complaint: !prev.has_service_complaint }))}
                                        className={`relative w-9 h-5 rounded-full transition-colors ${rideRatingAdjustments.has_service_complaint ? 'bg-red-500' : 'bg-slate-300'}`}
                                        data-testid={`complaint-toggle-${rideIndex}`}
                                      >
                                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${rideRatingAdjustments.has_service_complaint ? 'translate-x-4' : ''}`} />
                                      </button>
                                    </div>
                                    {/* No Pickup Toggle */}
                                    <div className="flex items-center justify-between">
                                      <label className="text-xs text-slate-700">Driver No-Show</label>
                                      <button
                                        onClick={() => setRideRatingAdjustments(prev => ({ ...prev, no_pickup_by_driver: !prev.no_pickup_by_driver }))}
                                        className={`relative w-9 h-5 rounded-full transition-colors ${rideRatingAdjustments.no_pickup_by_driver ? 'bg-red-500' : 'bg-slate-300'}`}
                                        data-testid={`no-pickup-toggle-${rideIndex}`}
                                      >
                                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${rideRatingAdjustments.no_pickup_by_driver ? 'translate-x-4' : ''}`} />
                                      </button>
                                    </div>
                                    {/* Action Buttons */}
                                    <div className="flex gap-2 pt-1">
                                      <button
                                        onClick={() => recalculateRideRating(ride.id, monthKey)}
                                        disabled={savingRideRating === ride.id}
                                        className="flex-1 flex items-center justify-center gap-1 text-xs text-slate-600 hover:bg-slate-100 border border-slate-300 rounded py-1.5 transition-colors disabled:opacity-50"
                                        data-testid={`recalculate-rating-btn-${rideIndex}`}
                                      >
                                        <RefreshCw className={`h-3 w-3 ${savingRideRating === ride.id ? 'animate-spin' : ''}`} />
                                        Ricalcola
                                      </button>
                                      <button
                                        onClick={() => saveRideRating(ride.id, monthKey)}
                                        disabled={savingRideRating === ride.id}
                                        className="flex-1 flex items-center justify-center gap-1 text-xs text-white bg-amber-500 hover:bg-amber-600 rounded py-1.5 transition-colors disabled:opacity-50"
                                        data-testid={`save-rating-btn-${rideIndex}`}
                                      >
                                        {savingRideRating === ride.id ? (
                                          <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                          <Save className="h-3 w-3" />
                                        )}
                                        Save
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="users-table">
            <thead>
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                  Name
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                  Email
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                  Phone
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                  GPS Tracker
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                  Role
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                    No users found matching your filters
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${user.is_active === false ? 'opacity-50 bg-slate-50' : ''}`}
                    data-testid={`user-row-${user.id}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center mr-3">
                          <User className="h-5 w-5 text-slate-600" />
                        </div>
                        <span className="text-sm font-medium text-slate-900">{user.name}</span>
                        {user.is_active === false && (
                          <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full" data-testid={`inactive-badge-${user.id}`}>
                            Inactive
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-slate-600">
                        <Mail className="h-4 w-4 mr-2 text-slate-400" />
                        {user.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {user.phone ? (
                        <div className="flex items-center text-sm text-slate-600">
                          <Phone className="h-4 w-4 mr-2 text-slate-400" />
                          {user.phone}
                        </div>
                      ) : (user.role === 'driver' || user.role === 'admin-driver') ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-300" data-testid={`missing-phone-${user.id}`}>
                          <Phone className="h-3 w-3" /> Telefono mancante
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {user.gps_device_id ? (
                        <a
                          href={`https://www.salin dgps.com/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium"
                          title={`Track ${user.name}`}
                        >
                          <MapPin className="h-4 w-4 mr-1" />
                          {user.gps_device_id}
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                      ) : (
                        <span className="text-sm text-slate-400">No GPS</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                          <Shield className="h-3 w-3 mr-1" />
                          {user.role}
                        </span>
                        {/* Show indicator if driver has no location restrictions */}
                        {(user.role === 'driver' || user.role === 'admin-driver') && user.location_restricted === false && (
                          <span 
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                            title="No GPS location restrictions"
                          >
                            <MapPinOff className="h-3 w-3 mr-1" />
                            No GPS
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => openEditDialog(user)}
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 hover:bg-blue-50 hover:text-blue-600 rounded-sm"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => setDevicesUser(user)}
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 hover:bg-emerald-50 hover:text-emerald-600 rounded-sm"
                          data-testid={`devices-user-${user.id}`}
                          title="Active Devices"
                        >
                          <Smartphone className="h-4 w-4" />
                        </Button>
                        {(user.role === 'driver' || user.role === 'admin-driver') && (
                          <Button
                            onClick={() => setExportDriver({ id: user.id, name: user.name })}
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 hover:bg-amber-50 hover:text-amber-600 rounded-sm"
                            data-testid={`export-rides-${user.id}`}
                            title="Export monthly rides"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                        {currentUser?.is_super_admin && (user.role === 'admin' || user.role === 'admin-driver') && (
                          <Button
                            onClick={() => setPermissionsUser(user)}
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 hover:bg-indigo-50 hover:text-indigo-600 rounded-sm"
                            data-testid={`permissions-user-${user.id}`}
                            title="Manage Permissions"
                          >
                            <Lock className="h-4 w-4" />
                          </Button>
                        )}
                        {currentUser?.id !== user.id && (
                          <Button
                            onClick={() => handleToggleActive(user)}
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 rounded-sm ${user.is_active === false ? 'hover:bg-emerald-50 hover:text-emerald-600 text-slate-400' : 'hover:bg-orange-50 hover:text-orange-600'}`}
                            data-testid={`toggle-active-${user.id}`}
                            title={user.is_active === false ? 'Reactivate account' : 'Deactivate account (hidden from driver lists, cannot log in)'}
                          >
                            {user.is_active === false ? <UserCheck className="h-4 w-4" /> : <UserX className="h-4 w-4" />}
                          </Button>
                        )}
                        <Button
                          onClick={() => handleDelete(user.id, user.name)}
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 hover:bg-red-50 hover:text-red-600 rounded-sm"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <span className="text-sm text-slate-500">
          Showing {filteredUsers.length} of {users.length} users
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAuditLogs(!showAuditLogs)}
          className="gap-2"
          data-testid="toggle-audit-logs"
        >
          <History className="h-4 w-4" />
          {showAuditLogs ? 'Hide' : 'Show'} Role Change History
        </Button>
      </div>

      {/* Audit Logs Section */}
      {showAuditLogs && (
        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="audit-logs-section">
          <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-slate-600" />
              <h3 className="font-semibold text-slate-900">Role Change History</h3>
              <span className="text-sm text-slate-500">({auditLogs.length} entries)</span>
            </div>
          </div>
          {auditLogs.length === 0 ? (
            <div className="px-6 py-12 text-center text-slate-500">
              No role changes recorded yet
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {auditLogs.map((log) => (
                <div key={log.id} className="px-6 py-4 hover:bg-slate-50 transition-colors" data-testid={`audit-log-${log.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium text-slate-900">{log.target_user_name}</span>
                        <span className="text-slate-400">({log.target_user_email})</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(log.old_role)}`}>
                          {log.old_role}
                        </span>
                        <ArrowRight className="h-3 w-3 text-slate-400" />
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(log.new_role)}`}>
                          {log.new_role}
                        </span>
                      </div>
                    </div>
                    <div className="text-right text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(log.timestamp).toLocaleString()}
                      </div>
                      <div className="mt-1">
                        by <span className="font-medium">{log.changed_by_name}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>

    {permissionsUser && (
      <PermissionsModal
        userId={permissionsUser.id}
        userName={permissionsUser.name}
        open={!!permissionsUser}
        onClose={() => setPermissionsUser(null)}
      />
    )}
    {devicesUser && (
      <UserDevicesDialog
        user={devicesUser}
        open={!!devicesUser}
        onOpenChange={(o) => { if (!o) setDevicesUser(null); }}
      />
    )}
    {exportDriver && (
      <DriverRidesExportDialog
        open={!!exportDriver}
        onOpenChange={(o) => { if (!o) setExportDriver(null); }}
        driverId={exportDriver.id}
        driverName={exportDriver.name}
        defaultYear={exportDriver.year}
        defaultMonth={exportDriver.month}
      />
    )}
    </>
  );
}